/**
 * Create the store with asynchronously loaded reducers
 */
import { createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { routerMiddleware } from 'connected-react-router/immutable';
import asyncDispatchMiddleware from 'utils/asyncDispatchMiddleware';
import history from 'utils/history';
import { REDUCER_KEY as AUTH_REDUCER_KEY } from 'utils/auth/reducer';
import { loadAuthSession } from 'utils/auth/storage';
import * as sessionStorageEx from 'utils/sessionStorageEx';
import { AuthSession } from 'utils/auth/types';
import { loadPersistedDatasetData } from 'data/persist/actions';
import createReducer from './reducers';

const actionSanitizer = (action) => {
  let sanitizedAction = action;
  if (/UI_STATE_SET:TxRegId_.+/.test(action.type)) {
    const state = { ...action.payload.state, currentFocusField: 'not deserialized' };
    const payload = { ...action.payload, state };
    sanitizedAction = { ...sanitizedAction, payload };
  }
  return sanitizedAction;
};

export function configureStore(initialState = {}, localHistory) {

  const reduxSagaMonitorOptions = {};

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  let composeEnhancers = compose;
  if (typeof window === 'object') {  // TODO: Should we disallow in production?
    /* eslint-disable no-underscore-dangle */
    if (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
      composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        actionSanitizer,
      });

      // TODO: Uncomment the code below to restore support for Redux Saga
      // TODO: Dev Tools once it supports redux-saga version 1.x.x
      // if (window.__SAGA_MONITOR_EXTENSION__)
      //   reduxSagaMonitorOptions = {
      //     sagaMonitor: window.__SAGA_MONITOR_EXTENSION__,
      // };
    }
    /* eslint-enable */
  }

  const sagaMiddleware = createSagaMiddleware(reduxSagaMonitorOptions);

  // Create the store with the following middleware
  //   sagaMiddleware: Makes redux-sagas work
  //   asyncDispatchMiddleware: Allows you to asynchronously dispatch actions from a reducer
  //   routerMiddleware: syncs the location/URL path to the state
  const middlewares = [
    // logger,
    sagaMiddleware,
    asyncDispatchMiddleware,
    routerMiddleware(localHistory),
  ];

  const enhancers = [applyMiddleware(...middlewares)];

  const reducer = createReducer();

  const store = createStore(
    reducer,
    initialState,
    composeEnhancers(...enhancers)
  );

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.injectedReducers = {}; // Reducer registry
  store.injectedSagas = {}; // Saga registry

  // TODO: I don't think it's really works - fix hot reload (keep in mind persistReducer)
  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(createReducer(store.injectedReducers));
    });
  }

  return store;
}

const initialState = {};

const persistedAuthSession = loadAuthSession();
if (persistedAuthSession) {
  initialState[AUTH_REDUCER_KEY] = persistedAuthSession;
}

const store = configureStore(initialState, history);

// TODO: remove loadPersistedDatasetData once Quicken stop using credentials blob for Wells Fargo
if (persistedAuthSession.datasetId) {
  store.dispatch(loadPersistedDatasetData());
}

//
// Try and grab an existing sessionStorage from another tab using storage events and if we get it store it in the state
//

function sessionStorageReceive(e) {
  const event = e || window.event;    // for IE
  if (!event.newValue || event.newValue.length <= 2) return;        // do nothing if no value to work with
  if (event.key === 'sessionStorage' && !sessionStorage.length) {

    // another tab sent data <- get it

    const data = JSON.parse(event.newValue);
    Object.keys(data).forEach((key) => sessionStorage.setItem(key, data[key]));
    const session = new AuthSession(sessionStorageEx.getAuthSession());
    store.getState()[AUTH_REDUCER_KEY] = session;
    window.location.replace('/'); // Go back to top level to re-evaluate authentication state
  }
}

// listen for changes to localStorage
if (window.addEventListener) {
  window.addEventListener('storage', sessionStorageReceive, false);
} else {
  window.attachEvent('onstorage', sessionStorageReceive);
}

// Ask other tabs for session storage
if (!sessionStorage.length) localStorage.setItem('getSessionStorage', Date.now());

export default store;
